// Syncfusion i18n configuration
import { L10n } from '@syncfusion/ej2-base';

// French month names mapping
const frenchMonths = {
  "January": "Janvier",
  "February": "Février", 
  "March": "Mars",
  "April": "Avril",
  "May": "Mai",
  "June": "Juin",
  "July": "Juillet",
  "August": "Août",
  "September": "Septembre",
  "October": "Octobre",
  "November": "Novembre",
  "December": "Décembre"
};

const PIVOTVIEW_FR = {
  Sum: "Somme",
  Count: "Nombre",
  Avg: "Moyenne",
  Min: "Min",
  Max: "Max",
  of: "de",
  grandTotal: "Total général",
  total: "Total",
  value: "Valeur",
  noValue: "Aucune valeur",
  row: "Ligne",
  column: "Colonne",
  blank: "(Vide)",
};

/**
 * Pivot chart axis titles and tooltips read "<aggregate> of <measure>".
 * ej2-pivotview resolves its own copy of @syncfusion/ej2-base, so strings
 * given to the app's L10n never reach a pivot: load them into the class of
 * the pivot's own localeObj and re-apply its locale. Call it from the
 * pivot's `load` event, before the chart and table are drawn.
 */
export const loadPivotLocale = (pivot) => {
  const localeObj = pivot?.localeObj;
  if (!localeObj || !pivot.locale?.startsWith("fr")) return;
  localeObj.constructor.load({ fr: { pivotview: PIVOTVIEW_FR } });
  localeObj.setLocale(pivot.locale);
};

// Configure Syncfusion L10n for all components
export const configureSyncfusionI18n = (language) => {
  const currentLang = language.startsWith('fr') ? 'fr' : 'en';
  
  // Set body attribute for CSS targeting IMMEDIATELY
  document.body.setAttribute('data-lang', currentLang);
  
  // Pre-hide all calendar titles if switching to French
  if (currentLang === 'fr') {
    const calendarTitles = document.querySelectorAll('.e-calendar .e-header .e-title');
    calendarTitles.forEach(title => {
      if (!title.hasAttribute('data-translated')) {
        title.style.display = 'none';
      }
    });
  }
  
  L10n.load({
    [currentLang]: {
      "daterangepicker": {
        "placeholder": currentLang === 'fr' ? "Sélectionnez une plage de dates" : "Select a date range",
        "startLabel": currentLang === 'fr' ? "Date de début" : "Start Date",
        "endLabel": currentLang === 'fr' ? "Date de fin" : "End Date",
        "applyText": currentLang === 'fr' ? "Appliquer" : "Apply",
        "cancelText": currentLang === 'fr' ? "Annuler" : "Cancel",
        "selectedDays": currentLang === 'fr' ? "Jours sélectionnés" : "Selected Days",
        "days": currentLang === 'fr' ? "Jours" : "Days",
        "customRange": currentLang === 'fr' ? "Plage personnalisée" : "Custom Range"
      },
      "calendar": {
        "today": currentLang === 'fr' ? "Aujourd'hui" : "Today"
      },
      "datepicker": {
        "placeholder": currentLang === 'fr' ? "Sélectionnez une date" : "Select a date",
        "today": currentLang === 'fr' ? "Aujourd'hui" : "Today"
      },
      "dialog": {
        "close": currentLang === 'fr' ? "Fermer" : "Close",
        "ok": currentLang === 'fr' ? "OK" : "OK",
        "cancel": currentLang === 'fr' ? "Annuler" : "Cancel",
        "yes": currentLang === 'fr' ? "Oui" : "Yes",
        "no": currentLang === 'fr' ? "Non" : "No"
      },
      "tooltip": {
        "close": currentLang === 'fr' ? "Fermer" : "Close"
      },
      "switch": {
        "on": currentLang === 'fr' ? "Activé" : "On",
        "off": currentLang === 'fr' ? "Désactivé" : "Off"
      },
      "dropdownbutton": {
        "close": currentLang === 'fr' ? "Fermer" : "Close"
      },
      "uploader": {
        "Browse": currentLang === 'fr' ? "Parcourir" : "Browse",
        "Clear": currentLang === 'fr' ? "Effacer" : "Clear",
        "Upload": currentLang === 'fr' ? "Importer" : "Upload",
        "dropFilesHint": currentLang === 'fr' ? "ou déposez les fichiers ici" : "Or drop files here",
        "invalidMinFileSize": currentLang === 'fr' ? "La taille du fichier est trop petite." : "File size is too small.",
        "invalidMaxFileSize": currentLang === 'fr' ? "La taille du fichier est trop grande." : "File size is too large.",
        "invalidFileType": currentLang === 'fr' ? "Le type de fichier n'est pas autorisé." : "File type is not allowed.",
        "uploadFailedMessage": currentLang === 'fr' ? "Échec de l'importation" : "Upload failed",
        "uploadSuccessMessage": currentLang === 'fr' ? "Importation réussie" : "Upload success",
        "removedFailedMessage": currentLang === 'fr' ? "Échec de la suppression" : "Remove failed",
        "removedSuccessMessage": currentLang === 'fr' ? "Suppression réussie" : "Remove success",
        "cancelToolTip": currentLang === 'fr' ? "Annuler" : "Cancel",
        "deleteToolTip": currentLang === 'fr' ? "Supprimer" : "Delete"
      }
    }
  });

    // Translate all Syncfusion elements in DOM
    if (currentLang === 'fr') {
      const translateSyncfusionElements = () => {
        // Target Syncfusion calendar headers
        const calendarSelectors = [
          '.e-calendar .e-header .e-title',
          '.e-daterangepicker .e-calendar .e-header .e-title',
          '.e-popup .e-calendar .e-header .e-title',
          '.e-datepicker .e-calendar .e-header .e-title'
        ];
        
        // Target other Syncfusion elements
        const otherSelectors = [
          '.e-dialog .e-dlg-header .e-title',
          '.e-tooltip .e-tip-content',
          '.e-switch .e-switch-label',
          '.e-dropdown-btn .e-btn-text'
        ];
        
        // Translate calendar month names
        calendarSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            // Hide element immediately if it contains English text
            const text = element.textContent;
            const hasEnglishMonth = Object.keys(frenchMonths).some(month => text.includes(month));
            
            if (hasEnglishMonth && !element.hasAttribute('data-translated')) {
              element.style.opacity = '0';
            }
            
            let translated = false;
            Object.keys(frenchMonths).forEach(englishMonth => {
              if (text.includes(englishMonth)) {
                element.textContent = text.replace(englishMonth, frenchMonths[englishMonth]);
                element.setAttribute('data-translated', 'true');
                element.style.display = ''; // Show the element
                element.style.opacity = '1';
                translated = true;
              }
            });
            if (translated) {
              element.classList.add('french-localized');
            }
          });
        });

        // Translate other Syncfusion elements
        otherSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            const text = element.textContent;
            let translated = false;
            
            // Common translations
            const translations = {
              'Close': 'Fermer',
              'OK': 'OK',
              'Cancel': 'Annuler',
              'Yes': 'Oui',
              'No': 'Non',
              'On': 'Activé',
              'Off': 'Désactivé',
              'Today': 'Aujourd\'hui',
              'Select a date': 'Sélectionnez une date'
            };
            
            Object.keys(translations).forEach(english => {
              if (text.includes(english)) {
                element.textContent = text.replace(english, translations[english]);
                element.setAttribute('data-translated', 'true');
                translated = true;
              }
            });
            
            if (translated) {
              element.classList.add('french-localized');
            }
          });
        });
      };

    // Run immediately and on DOM changes
    translateSyncfusionElements(); // Run immediately without delay
    
    // Also run on next frame to catch any delayed renders
    requestAnimationFrame(translateSyncfusionElements);
    
    const observer = new MutationObserver(() => {
      translateSyncfusionElements(); // Run immediately without delay
      requestAnimationFrame(translateSyncfusionElements); // Also run on next frame
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Store observer for cleanup
    window.syncfusionI18nObserver = observer;
  } else {
    // Clean up observer when switching to English
    if (window.syncfusionI18nObserver) {
      window.syncfusionI18nObserver.disconnect();
      window.syncfusionI18nObserver = null;
    }
  }
};

export { frenchMonths };
